import orderBy from 'lodash/orderBy';
import uniq from 'lodash/uniq';

import { FlowType } from 'fable/flowEditor';

export enum SystemVariableId {
  Body = 'body',
  CallerNumber = 'caller_number',
  CCEmails = 'cc_emails',
  Channel = 'channel',
  CSID = 'csid',
  Date = 'date',
  DateRange = 'date_range',
  Direction = 'direction',
  FromEmail = 'from_email',
  FromName = 'from_name',
  Language = 'language',
  ReceivingNumber = 'receiving_number',
  Subject = 'subject',
  SubjectOrBody = 'subject_or_body',
  SpamProbability = 'spam_probability',
  ToEmail = 'to_email',
  EnduserExternalId = 'enduser_external_id',
  ContactCustomAttributePrefix = 'contact_custom_attribute:',
  Set = 'set',
}

export enum SystemVariableType {
  Input = 'input',
  Scope = 'scope',
  System = 'system',
}

type ScopeVarsT = {
  available: string[];
};

export type PropsT = {
  scopeVars: ScopeVarsT;
  flowType: FlowType;
};

export type FlowVarItemT = {
  name: string;
  content: string;
};

export type FlowCategoryT = {
  label: string;
  items: FlowVarItemT[];
};

export type FlowCategorySelectItem = {
  name: string;
  id: `${SystemVariableType.Scope | SystemVariableType.System}:${
    | string
    | SystemVariableId}`;
  description: string;
};

export type FlowCategoriesT = FlowCategoryT[];

export type CallOrEmailFlowTypeT =
  | FlowType.FLOW_TYPE_CALL
  | FlowType.FLOW_TYPE_EMAIL;

export const FLOW_TYPE_CALL = 'FLOW_TYPE_CALL';
export const FLOW_TYPE_EMAIL = 'FLOW_TYPE_EMAIL';
const REGEX_VALID_NAME = /(:)/;

export const isValidVariableName = (name: string): boolean => {
  return !REGEX_VALID_NAME.test(name);
};

export const getVariableNameErrorMessage = (name: string): null | string => {
  const match = REGEX_VALID_NAME.exec(name);
  if (match === null) {
    return null;
  }
  return `Contains invalid characters "${match[1]}"`;
};

export function getSystemVariableString(
  id: SystemVariableId,
): `${SystemVariableType.System}:${SystemVariableId}` {
  return `${SystemVariableType.System}:${id}`;
}

export function checkAndExtractCustomAttribute(
  maybeRawAttribute?: unknown,
): [`${SystemVariableType.System}:${SystemVariableId}`, string] | [] {
  if (
    typeof maybeRawAttribute !== 'string' ||
    !maybeRawAttribute.includes(SystemVariableId.ContactCustomAttributePrefix)
  ) {
    return [];
  }

  const attrVariable = getSystemVariableString(
    SystemVariableId.ContactCustomAttributePrefix,
  );
  const maybeId = maybeRawAttribute.replace(attrVariable, '');
  if (!maybeId) {
    return [];
  }

  return [attrVariable, maybeId];
}

export const getAvailableFlowVariables = (props: PropsT): FlowCategoriesT => {
  const { scopeVars, flowType } = props;
  const flowCategories = [
    {
      label: 'Flow variables',
      items: orderBy(
        uniq(scopeVars.available).map(
          (scopeVar: string): FlowVarItemT => ({
            name: `scope:${scopeVar}`,
            content: scopeVar,
          }),
          'name',
        ),
      ),
    },
  ];

  switch (flowType) {
    case FlowType.FLOW_TYPE_CALL:
      flowCategories.push({
        label: 'Conversation data',
        items: [
          {
            name: getSystemVariableString(SystemVariableId.CSID),
            content: 'Conversation ID',
          },
          {
            name: getSystemVariableString(SystemVariableId.CallerNumber),
            content: 'Caller number',
          },
          {
            name: getSystemVariableString(SystemVariableId.ReceivingNumber),
            content: 'Receiving number',
          },
          {
            name: getSystemVariableString(SystemVariableId.EnduserExternalId),
            content: 'External ID',
          },
        ],
      });
      break;
    case FlowType.FLOW_TYPE_EMAIL:
      flowCategories.push({
        label: 'Conversation data',
        items: [
          {
            name: getSystemVariableString(SystemVariableId.CSID),
            content: 'Conversation ID',
          },
          {
            name: getSystemVariableString(SystemVariableId.Channel),
            content: 'Channel',
          },
          {
            name: getSystemVariableString(SystemVariableId.Direction),
            content: 'Direction',
          },
          {
            name: getSystemVariableString(SystemVariableId.FromEmail),
            content: 'From email',
          },
          {
            name: getSystemVariableString(SystemVariableId.ToEmail),
            content: 'To email',
          },
          {
            name: getSystemVariableString(SystemVariableId.FromName),
            content: 'From name',
          },
          {
            name: getSystemVariableString(SystemVariableId.SubjectOrBody),
            content: 'Subject or body',
          },
          {
            name: getSystemVariableString(SystemVariableId.Subject),
            content: 'Subject',
          },
          {
            name: getSystemVariableString(SystemVariableId.Body),
            content: 'Body',
          },
          {
            name: getSystemVariableString(SystemVariableId.CCEmails),
            content: 'CC emails',
          },
          {
            name: getSystemVariableString(SystemVariableId.Language),
            content: 'Language',
          },
          {
            name: getSystemVariableString(SystemVariableId.EnduserExternalId),
            content: 'External ID',
          },
        ],
      });
      break;
    case FlowType.FLOW_TYPE_MESSENGER:
      flowCategories.push({
        label: 'Conversation data',
        items: [
          {
            name: getSystemVariableString(SystemVariableId.Language),
            content: 'Language',
          },
          {
            name: getSystemVariableString(SystemVariableId.FromEmail),
            content: 'From email',
          },
          {
            name: getSystemVariableString(SystemVariableId.FromName),
            content: 'From name',
          },
          {
            name: getSystemVariableString(SystemVariableId.CSID),
            content: 'Conversation ID',
          },
          {
            name: getSystemVariableString(SystemVariableId.EnduserExternalId),
            content: 'External ID',
          },
        ],
      });
      break;
  }

  return flowCategories;
};
